import "../i18n/constants.js";
import { getPosixLocaleId } from "../i18n/utils.js";
import "../utils/index.js";
import "../i18n/index.js";
import { definedEntries } from "../common/utils/objects.js";
import { DEFAULT_FAVICON, DEFAULT_VIEWPORT_PARAMS, VIEWPORT_PARAM_NAMES } from "./constants.js";
import { entries, isDefined } from "remeda";
// src/metadata/utils.ts
const serializeViewportParams = (params) => entries(VIEWPORT_PARAM_NAMES).filter(([key]) => isDefined(params[key])).map(([key, name]) => `${name}=${params[key]}`).join(", ");
const getNamedMetaTagAttrsList = (contents) => definedEntries(contents).map(([name, content]) => ({
	name,
	content
}));
const getPropertyMetaTagAttrsList = (contents) => definedEntries(contents).map(([property, content]) => ({
	property,
	content
}));
const getPageDocumentMetadata = ({ baseUrl, localeId, posixLocaleIdMap, routePath, staticParams, supportedLocaleIds }) => {
	return {
		...staticParams,
		baseUrl,
		image: {
			...staticParams.image,
			secureUrl: baseUrl.includes("https://") ? baseUrl + staticParams.image.src : void 0
		},
		locale: getPosixLocaleId({
			localeId,
			posixLocaleIdMap,
			supportedLocaleIds
		}),
		url: baseUrl + (routePath.endsWith("/") ? routePath.slice(0, -1) : routePath)
	};
};
const getJsonLdScript = (jsonLd) => ({
	type: "application/ld+json",
	children: JSON.stringify(jsonLd).replaceAll("<", "\\u003c")
});
const getDocumentHeadManifest = ({ favicon = DEFAULT_FAVICON, isCharsetSetElsewhere, isOgTitleSetElsewhere, isRoutingLocaleDependent, jsonLd = [], metadata, ogSiteName, posixLocaleIdMap, routePath, twitterHandles, viewportParams = DEFAULT_VIEWPORT_PARAMS }) => {
	const { author, baseUrl, description, image, locale, title, url } = metadata;
	const normalizedRoutePath = routePath === "/" ? "" : routePath;
	const localizedUrl = (localeId) => localeId === "en" ? `${baseUrl}${normalizedRoutePath}` : `${baseUrl}/${localeId}${normalizedRoutePath}`;
	const localeEntries = isRoutingLocaleDependent ? entries(posixLocaleIdMap) : [];
	const alternateLinks = isRoutingLocaleDependent ? [...localeEntries.map(([localeId]) => ({
		rel: "alternate",
		href: localizedUrl(localeId),
		hrefLang: localeId,
		type: "text/html"
	})), {
		rel: "alternate",
		href: localizedUrl("en"),
		hrefLang: "x-default",
		type: "text/html"
	}] : [];
	return {
		meta: [
			...isCharsetSetElsewhere ? [] : [{ charSet: "utf-8" }],
			...getNamedMetaTagAttrsList({
				viewport: serializeViewportParams(viewportParams),
				description,
				author,
				"twitter:title": title,
				"twitter:description": description,
				"twitter:url": url,
				"twitter:card": image.secureUrl ? "summary" : void 0,
				"twitter:image": image.secureUrl,
				"twitter:image:alt": image.secureUrl ? image.alt : void 0,
				"twitter:site": twitterHandles?.site,
				"twitter:creator": twitterHandles?.author
			}),
			...getPropertyMetaTagAttrsList({
				"og:title": isOgTitleSetElsewhere ? void 0 : title,
				"og:description": description,
				"og:url": url,
				"og:type": "website",
				"og:site_name": ogSiteName,
				"og:locale": locale,
				"og:image": `${baseUrl}${image.src}`,
				"og:image:alt": image.alt,
				"og:image:type": image.type,
				"og:image:width": image.width,
				"og:image:height": image.height,
				"og:image:secure_url": image.secureUrl
			}),
			...localeEntries.filter(([, posixLocaleId]) => posixLocaleId !== locale).map(([, posixLocaleId]) => ({
				property: "og:locale:alternate",
				content: posixLocaleId
			}))
		],
		links: [
			{
				rel: "icon",
				type: favicon.type,
				href: favicon.href
			},
			{
				rel: "canonical",
				href: url
			},
			...alternateLinks
		],
		scripts: jsonLd.map(getJsonLdScript)
	};
};
const getBlogPostingJsonLd = ({ url, headline, description, image, datePublished, dateModified, author, publisher, blogUrl }) => ({
	"@context": "https://schema.org",
	"@type": "BlogPosting",
	mainEntityOfPage: url,
	url,
	headline,
	description,
	image,
	datePublished,
	...dateModified ? { dateModified } : {},
	author: {
		"@type": "Person",
		...author
	},
	publisher: {
		"@type": "Organization",
		...publisher
	},
	isPartOf: {
		"@type": "Blog",
		url: blogUrl
	}
});
const getBlogJsonLd = ({ url, name, description, publisher }) => ({
	"@context": "https://schema.org",
	"@type": "Blog",
	url,
	name,
	description,
	publisher: {
		"@type": "Organization",
		...publisher
	}
});
const getBreadcrumbListJsonLd = (breadcrumbs) => ({
	"@context": "https://schema.org",
	"@type": "BreadcrumbList",
	itemListElement: breadcrumbs.map(({ name, url }, index) => ({
		"@type": "ListItem",
		position: index + 1,
		name,
		item: url
	}))
});
export { getBlogJsonLd, getBlogPostingJsonLd, getBreadcrumbListJsonLd, getDocumentHeadManifest, getJsonLdScript, getPageDocumentMetadata, serializeViewportParams };

//# sourceMappingURL=utils.js.map